import styled from '@emotion/styled';
import React, { forwardRef } from 'react';

import { getColorValue, getFocusRing, getRadius, u } from '../../utilities';

const RadioLabel = styled.label`
  display: block;
  position: relative;

  .RadioBox:after {
    background-color: transparent;
  }
`;

const RadioInput = styled.input`
  position: absolute;
  opacity: 0;

  &:not(:disabled) {
    cursor: pointer;

    & ~ .RadioBox {
      border: 2px solid ${getColorValue('body')};
    }

    &:checked ~ .RadioBox {
      border: 2px solid ${getColorValue('body')};
    }
  }

  &:disabled,
  &:disabled ~ .RadioBox {
    pointer-events: none;
  }

  &:disabled ~ .RadioBox {
    background-color: ${getColorValue('disabledBackground')};
  }

  &:checked {
    & ~ .RadioBox:after {
      background-color: ${getColorValue('blurple')};
    }

    &:disabled ~ .RadioBox:after {
      background-color: ${getColorValue('disabledContent')};
    }
  }

  &:focus-visible ~ .RadioBox {
    ${getFocusRing()};
  }
`;

const RadioBox = styled.span`
  cursor: pointer;
  width: ${u(2.25)};
  height: ${u(2.25)};
  ${getRadius('full')};
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;

  &:after {
    content: '';
    width: ${u(1)};
    height: ${u(1)};
    ${getRadius('full')};
    background-color: ${getColorValue('white')};
  }
`;

const Radio = forwardRef(
  (
    {
      isDisabled,
      isChecked,
      onFocus,
      onChange,
      onBlur,
      ...props
    }: RadioProps & React.ComponentProps<typeof RadioInput>,
    ref: React.ForwardedRef<HTMLInputElement>
  ) => {
    return (
      <RadioLabel htmlFor={props.id}>
        <RadioInput
          type="radio"
          disabled={isDisabled}
          checked={isChecked}
          onFocus={onFocus}
          onChange={onChange}
          onBlur={onBlur}
          ref={ref}
          {...props}
        />
        <RadioBox className="RadioBox" />
      </RadioLabel>
    );
  }
);

type RadioProps = {
  isChecked?: boolean;
  isDisabled?: boolean;
  onFocus?: React.ReactEventHandler;
  onChange?: React.ReactEventHandler;
  onBlur?: React.ReactEventHandler;
};

export default Radio;
